import { useCallback, useEffect, useState } from "react";
import { message } from "antd";
import { uploadFile } from "../../../api/attachmentApi";
import { airlineScheduleIngestApi, ingestErrorMessage } from "../../../api/v3/airlineScheduleIngestApi";

const POLL_MS = 8000;

function isPdfFile(file) {
    const name = String(file?.name || "").toLowerCase();
    const type = String(file?.type || "").toLowerCase();
    return name.endsWith(".pdf") || type === "application/pdf";
}

export function useAirlineScheduleIngest({ enabled = true } = {}) {
    const [tasks, setTasks] = useState([]);
    const [loading, setLoading] = useState(false);

    const refetch = useCallback(async () => {
        if (!enabled) {
            setTasks([]);
            return [];
        }
        const response = await airlineScheduleIngestApi.listWorkspaceTasks();
        const items = response?.data?.items || [];
        setTasks(items);
        return items;
    }, [enabled]);

    useEffect(() => {
        if (!enabled) {
            return undefined;
        }
        let cancelled = false;
        setLoading(true);
        refetch()
            .catch((error) => {
                if (!cancelled) {
                    message.error(ingestErrorMessage(error, "接入任务列表暂时打不开"));
                }
            })
            .finally(() => {
                if (!cancelled) {
                    setLoading(false);
                }
            });
        return () => {
            cancelled = true;
        };
    }, [enabled, refetch]);

    useEffect(() => {
        if (!enabled) {
            return undefined;
        }
        const hasRunning = tasks.some((item) => item.status === "running");
        if (!hasRunning) {
            return undefined;
        }
        const timer = window.setInterval(() => {
            refetch().catch(() => {});
        }, POLL_MS);
        return () => window.clearInterval(timer);
    }, [enabled, tasks, refetch]);

    const startFromFile = useCallback(
        async (file, { replaceTaskId } = {}) => {
            if (!isPdfFile(file)) {
                message.error("只收 PDF");
                return null;
            }
            const hide = message.loading("正在上传航司班期…", 0);
            try {
                const uploaded = await uploadFile(file);
                const fileId = uploaded?.object_key || uploaded?.storage_path;
                if (!fileId) {
                    throw new Error("文件已上传，但没有拿到存储位置");
                }
                const response = await airlineScheduleIngestApi.startTask({
                    file_name: file.name,
                    file_id: fileId,
                    replace_task_id: replaceTaskId || undefined,
                });
                message.success("已收下，正在识别。可以先离开。");
                await refetch();
                return response.data;
            } catch (error) {
                message.error(ingestErrorMessage(error, "接入发起失败"));
                return null;
            } finally {
                hide();
            }
        },
        [refetch],
    );

    const retryTask = useCallback(
        async (taskId) => {
            try {
                await airlineScheduleIngestApi.retryTask(taskId);
                message.success("已重新识别");
                await refetch();
            } catch (error) {
                message.error(ingestErrorMessage(error, "重新识别失败"));
            }
        },
        [refetch],
    );

    const discardTask = useCallback(
        async (taskId) => {
            try {
                await airlineScheduleIngestApi.discardTask(taskId);
                message.success("已丢弃这次接入");
                await refetch();
            } catch (error) {
                message.error(ingestErrorMessage(error, "丢弃失败"));
            }
        },
        [refetch],
    );

    return {
        tasks,
        loading,
        refetch,
        startFromFile,
        retryTask,
        discardTask,
        isPdfFile,
    };
}
